import { getProductImages, getProductDescriptions } from '@/services/api.service';

export default {
    namespaced: true,
    state: {
        productImages: [],
        fetchImageSize: 10,
        fetchImagePage: 0,
        productDescriptions: [],
        fetchDescPage: 0,
        fetchDescSize: 50,
        _descriptionsLoaded: false,
    },
    getters: {
        getProductImageById: (state) => (productId) => {
            try {
                if (!state.productImages.length) return '';
                const productImages = state.productImages
                    .filter(image => String(image.product_id) === String(productId) && image.image_url && Number(image.sheet_id) === 2)
                    .sort((a, b) => {
                        const primaryDiff = Number(b.is_primary || 0) - Number(a.is_primary || 0);
                        if (primaryDiff !== 0) return primaryDiff;

                        const sortOrderDiff = Number(a.sort_order || 0) - Number(b.sort_order || 0);
                        if (sortOrderDiff !== 0) return sortOrderDiff;

                        return Number(a.image_id || a.id || 0) - Number(b.image_id || b.id || 0);
                    });

                return productImages[0]?.image_url || '';
            } catch (e) {
                return ''
            }
        },
        getProductImagesById: (state) => (productId) => {
            try {
                if (!state.productImages.length) return [];

                return state.productImages
                    .filter(image => String(image.product_id) === String(productId) && image.image_url && Number(image.sheet_id) === 2)
                    .sort((a, b) => {
                        const primaryDiff = Number(b.is_primary || 0) - Number(a.is_primary || 0);
                        if (primaryDiff !== 0) return primaryDiff;

                        const sortOrderDiff = Number(a.sort_order || 0) - Number(b.sort_order || 0);
                        if (sortOrderDiff !== 0) return sortOrderDiff;

                        return Number(a.image_id || a.id || 0) - Number(b.image_id || b.id || 0);
                    });
            } catch (e) {
                return [];
            }
        },
        getProductDescById: (state, _getters, _rootState, rootGetters) => (productId) => {
            try {
                // 取得 product 的原始描述作為回退資料（同時查找一般商品和套餐）
                const products = rootGetters['spreadsheet/products'] || []
                const combos = rootGetters['spreadsheet/combos'] || []
                const product = products.find(p => p.id === productId) || combos.find(c => c.id === productId)
                const originalDescription = product?.description || ''

                // 1. 優先檢查 Product Desc API 資料
                if (state.productDescriptions && state.productDescriptions.length > 0) {
                    const productDesc = state.productDescriptions.find(desc => desc.product_id === productId)

                    // 驗證資料有效性：sheet_id === 2 且 product_desc !== null
                    if (productDesc &&
                        productDesc.sheet_id === 2 &&
                        productDesc.product_desc !== null &&
                        productDesc.product_desc !== undefined) {
                        return productDesc.product_desc
                    }
                }

                // 2. 回退到 Google Sheets 原始描述
                return originalDescription

            } catch (e) {
                // 發生錯誤時回傳 Google Sheets 原始描述，確保向後相容性
                try {
                    const products = rootGetters['spreadsheet/products'] || []
                    const combos = rootGetters['spreadsheet/combos'] || []
                    const product = products.find(p => p.id === productId) || combos.find(c => c.id === productId)
                    return product?.description || ''
                } catch (fallbackError) {
                    // 如果連回退都失敗，回傳空字串
                    return ''
                }
            }
        },
    },
    mutations: {
        setProductImages(state, productImages) {
            state.productImages = productImages;
        },
        setFetchImagePage(state, page) {
            state.fetchImagePage = page;
        },
        setProductDescriptions(state, descriptions) {
            state.productDescriptions = descriptions;
        },
        setFetchDescPage(state, page) {
            state.fetchDescPage = page;
        },
        setDescriptionsLoaded(state, loaded) {
            state._descriptionsLoaded = loaded;
        },
        // 重置商品描述載入狀態，用於強制重新載入
        resetProductDescriptions(state) {
            state.productDescriptions = [];
            state._descriptionsLoaded = false;
        }
    },
    actions: {
        async getProductImages({ commit, state, rootGetters }, productIds) {
            const data = {
                page: state.fetchImagePage,
                page_size: 0 // 預設值
            }

            if (productIds) {
                const productIdList = Array.isArray(productIds) ? productIds : [productIds];
                // product_id 需為陣列 就算是只有一個 id 也要包成陣列
                data.product_id = productIdList;
                // 商品圖片最多 10 張，page_size 需涵蓋同商品多圖
                data.page_size = productIdList.length * 10;
            } else {
                // 沒有傳入 productIds，載入所有商品
                const allProductIds = [
                    ...rootGetters['spreadsheet/products'].map(product => product.id),
                    ...rootGetters['spreadsheet/combos'].map(combo => combo.id)
                ];
                data.product_id = allProductIds;
                // 商品圖片最多 10 張，page_size 需涵蓋同商品多圖
                data.page_size = allProductIds.length * 10;
            }

            console.log('data', data);
            const res = await getProductImages(data);
            console.log('get product images', res.data.data);
            if (res?.data?.data?.msg === 'Failed') {
                throw res?.data?.data?.err_msg
            }
            const productImages = res.data.data.product_image_list.filter(image => image);
            commit('setProductImages', productImages);
            // if (productImages.length) {
            //     commit('setProductImages', [...state.productImages, ...productImages]);
            // }
        },
        async fetchProductDescriptions({ commit, state, rootGetters }) {
            try {

                // 計算 page_size：取得所有商品和套餐的總數量，與 getProductImages 保持一致
                const productsCount = rootGetters['spreadsheet/products']?.length || 0;
                const combosCount = rootGetters['spreadsheet/combos']?.length || 0;
                const totalProductCount = productsCount + combosCount;

                const data = {
                    page: state.fetchDescPage,
                    page_size: totalProductCount || state.fetchDescSize // 如果沒有商品資料，使用預設值
                };

                console.log(`Fetching product descriptions: ${totalProductCount} total products (${productsCount} products + ${combosCount} combos)`);

                const res = await getProductDescriptions(data);

                // 檢查 API 回應是否成功
                if (res?.data?.data?.msg === 'Failed') {
                    console.warn('Product descriptions API returned failure:', res?.data?.data?.error_msg);
                    // API 回應失敗時，清空資料讓系統回退到 Google Sheets
                    commit('setProductDescriptions', []);
                    return [];
                }

                // 檢查回應資料結構
                if (!res?.data?.data?.product_desc_list) {
                    console.warn('Product descriptions API returned invalid data structure');
                    commit('setProductDescriptions', []);
                    return [];
                }

                const productDescriptions = res.data.data.product_desc_list || [];

                // 驗證資料品質：過濾掉無效的描述資料
                const validDescriptions = productDescriptions.filter(desc => {
                    return desc &&
                        desc.product_id &&
                        desc.sheet_id !== undefined &&
                        desc.product_desc !== undefined;
                });

                commit('setProductDescriptions', validDescriptions);

                if (validDescriptions.length !== productDescriptions.length) {
                    console.warn(`Filtered ${productDescriptions.length - validDescriptions.length} invalid product descriptions`);
                }

                return validDescriptions;

            } catch (error) {
                console.error('fetchProductDescriptions error:', error);

                // 網路錯誤或其他異常：確保向後相容性
                // 清空 productDescriptions，讓 getter 回退到 Google Sheets 資料
                commit('setProductDescriptions', []);

                // 不拋出異常，讓應用程式繼續運行
                // 這確保了即使 API 完全失敗，現有功能仍能正常工作
                return [];
            }
        },

        // 安全的初始化方法，可以被組件呼叫來確保商品描述已載入
        async ensureProductDescriptionsLoaded({ dispatch, state, rootGetters }) {
            try {
                // 檢查是否有商品資料，如果沒有則不需要載入描述
                const products = rootGetters['spreadsheet/products'] || [];
                if (products.length === 0) {
                    return [];
                }

                // 如果還沒載入過，則載入
                if (!state._descriptionsLoaded) {
                    return await dispatch('fetchProductDescriptions');
                }

                return state.productDescriptions;
            } catch (error) {
                console.error('ensureProductDescriptionsLoaded error:', error);
                // 發生錯誤時不影響現有功能
                return [];
            }
        },

        // 重置並重新載入商品描述
        async refreshProductDescriptions({ commit, dispatch }) {
            try {
                console.log('Refreshing product descriptions...');

                // 重置載入狀態
                commit('resetProductDescriptions');

                // 重新載入
                return await dispatch('fetchProductDescriptions');
            } catch (error) {
                console.error('refreshProductDescriptions error:', error);
                return [];
            }
        },

        // 當商品資料更新時呼叫此方法來重置描述載入狀態
        onProductDataUpdated({ commit }) {
            console.log('Product data updated, resetting descriptions loaded state');
            commit('resetProductDescriptions');
        }
    },
}
